import { fetchJson } from './api'
import { archiveFallback } from '@vod/core/playback'
import type { PlayerCamera } from './cameras'
import type { ArchiveEvent } from './events'
import type { CalendarData, DayData, Recording, Segment } from './types'
import { dayBounds } from './time'

export function dayPlayhead(
  data: Pick<DayData, 'start' | 'end' | 'recordings'>,
  segments: Segment[],
  utc: number | null,
) {
  const preferred = data.recordings.find((r) => r.previewId) ?? data.recordings[0]
  const fallback = preferred ? Math.max(data.start, preferred.start) : data.start
  if (utc === null) return fallback
  const last = segments
    .filter((segment) => segment.end > data.start && segment.start < data.end)
    .at(-1)
  if (last && utc >= Math.min(data.end, last.end)) {
    return Math.max(data.start, last.start)
  }
  return Math.max(data.start, utc)
}

export function mergeCameraDays(days: DayData[], archiveOnly = false) {
  const select = (recording: Recording): Recording | null =>
    !archiveOnly || recording.playbackKind === 'archive'
      ? recording
      : archiveFallback(recording)
  days = days.map((day) => ({
    ...day,
    recordings: day.recordings.flatMap((recording) => select(recording) ?? []),
    following: day.following ? select(day.following) : null,
    followingRecordings: (
      day.followingRecordings ?? (day.following ? [day.following] : [])
    ).flatMap((recording) => select(recording) ?? []),
    audioRecordings: (day.audioRecordings ?? []).flatMap(
      (recording) => select(recording) ?? [],
    ),
    followingAudioRecordings: (day.followingAudioRecordings ?? []).flatMap(
      (recording) => select(recording) ?? [],
    ),
  }))
  const recordings = [
    ...new Map(
      days.flatMap((day) => day.recordings).map((recording) => [recording.id, recording]),
    ).values(),
  ].sort((a, b) => a.start - b.start || a.id.localeCompare(b.id))
  const followingRecordings = days
    .flatMap((day) => day.followingRecordings ?? [])
    .sort((a, b) => a.start - b.start || a.id.localeCompare(b.id))
  const audioRecordings = [
    ...new Map(
      days
        .flatMap((day) => day.audioRecordings ?? [])
        .map((recording) => [recording.id, recording]),
    ).values(),
  ].sort((a, b) => a.start - b.start || a.id.localeCompare(b.id))
  const followingAudioRecordings = days
    .flatMap((day) => day.followingAudioRecordings ?? [])
    .sort((a, b) => a.start - b.start || a.id.localeCompare(b.id))
  const previousDays = days
    .flatMap((day) => (day.previousDay ? [day.previousDay] : []))
    .sort()
  const nextDays = days.flatMap((day) => (day.nextDay ? [day.nextDay] : [])).sort()
  return {
    ...days[0],
    recordings,
    following: followingRecordings[0] ?? null,
    followingRecordings,
    ...(audioRecordings.length || followingAudioRecordings.length
      ? { audioRecordings, followingAudioRecordings }
      : {}),
    previousDay: previousDays.at(-1) ?? null,
    nextDay: nextDays[0] ?? null,
  }
}

export async function fetchCameraDay(
  apiBase: string,
  camera: PlayerCamera,
  day: string,
  signal?: AbortSignal,
  archiveOnly = false,
): Promise<DayData & { followingRecordings: Recording[] }> {
  return mergeCameraDays(
    [
      await fetchJson<DayData>(
        `${apiBase}/api/cameras/${encodeURIComponent(camera.id)}/day?date=${day}`,
        signal,
      ),
    ],
    archiveOnly,
  )
}

export function mergeCameraCalendars(
  calendars: CalendarData[],
  _timezone: string,
): CalendarData {
  const days: CalendarData['days'] = {}
  for (const calendar of calendars) {
    for (const [day, value] of Object.entries(calendar.days)) {
      if (value) days[day] = true
    }
  }
  return { month: calendars[0].month, days }
}

export async function fetchCameraCalendar(
  apiBase: string,
  camera: PlayerCamera,
  month: string,
  signal?: AbortSignal,
) {
  return fetchJson<CalendarData>(
    `${apiBase}/api/cameras/${encodeURIComponent(camera.id)}/calendar?month=${month}`,
    signal,
  )
}

export async function fetchCameraEvents(
  endpoint: string,
  camera: PlayerCamera,
  day: string,
  signal?: AbortSignal,
) {
  const bounds = dayBounds(day, camera.timezone)
  const events: ArchiveEvent[] = []
  for (let offset = 0; ; offset += 100) {
    const page = await fetchJson<ArchiveEvent[]>(
      `${endpoint}?camera=${encodeURIComponent(camera.id)}&start=${bounds.start}&end=${bounds.end}&offset=${offset}`,
      signal,
    )
    events.push(...page)
    if (page.length < 100) break
  }
  return events.sort((a, b) => a.start - b.start || a.id.localeCompare(b.id))
}
